import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export const SUCCESS_INBOX_THREADS_QUERY_KEY = 'admin/successInbox/threads';
export const SUCCESS_INBOX_PILOT_QUERY_KEY = 'admin/successInbox/pilot';

export type SuccessInboxCategory =
	| 'SupportBug'
	| 'Howto'
	| 'Production'
	| 'Billing'
	| 'Website'
	| 'Meeting'
	| 'Relationship'
	| 'Ads'
	| 'Advisory'
	| 'NotClient'
	| 'Unclassified';

export type SuccessInboxState = 'Open' | 'Resolved';

export type SuccessInboxDisposition = 'None' | 'Ignored' | 'Resolved' | 'SentToSupport';

export type SuccessInboxSenderKind = 'CustomerUser' | 'CustomerContact' | 'InternalUser' | 'LevitateSystem' | 'Unknown';

export interface ISuccessInboxThread {
	id: string;
	/** The rep whose mailbox this came from: ingestion runs under their context. */
	creatorId: string;
	threadKey: string;
	subject?: string;
	senderEmail?: string;
	senderName?: string;
	senderKind: SuccessInboxSenderKind;
	customerAccountId?: string;
	customerAccountName?: string;
	contactId?: string;
	firstReceivedAt: string;
	lastReceivedAt: string;
	messageCount: number;
	supportOnThread: boolean;
	looksLikeClientCampaign: boolean;
	/** What the thread is about. Independent of whether anyone still owes an answer. */
	category: SuccessInboxCategory;
	/** The classifier's reading. Distinct from `disposition`, which is what a person decided. */
	state: SuccessInboxState;
	urgent: boolean;
	autoCcRecommended: boolean;
	rationale?: string;
	summary?: string;
	needsAttention: boolean;
	classifiedAt?: string;
	disposition: SuccessInboxDisposition;
	dispositionByUserId?: string;
	dispositionAt?: string;
	dispositionNote?: string;
	escalatedAt?: string;
	escalatedToEmail?: string;
}

export type SuccessInboxEscalationTarget = 'Support' | 'Website' | 'Ads';

export type SuccessInboxClientHandoff = 'TeamContactsClient' | 'TeamRepliesToRep';

export interface ISuccessInboxEscalationDraft {
	suggestedTarget: SuccessInboxEscalationTarget;
	availableTargets: SuccessInboxEscalationTarget[];
	suggestedHandoff: SuccessInboxClientHandoff;
	notifyClient: boolean;
	clientMessage?: string;
	clientEmail?: string;
	sendingFromEmail?: string;
	repName?: string;
	senderIsRep: boolean;
}

export interface ISuccessInboxEscalationRequest {
	clientMessage?: string;
	handoff: SuccessInboxClientHandoff;
	notifyClient: boolean;
	note?: string;
	target: SuccessInboxEscalationTarget;
}

export interface ISuccessRep {
	userId: string;
	name: string;
	email: string;
}

export function useSuccessInboxThreadsQuery({
	categories = [],
	enabled = true,
	openOnly = true,
	refetchInterval,
	supportWouldHelp = false,
	urgentOnly = false,
	userId,
}: {
	categories?: SuccessInboxCategory[];
	enabled?: boolean;
	openOnly?: boolean;
	refetchInterval?: number | false;
	supportWouldHelp?: boolean;
	urgentOnly?: boolean;
	userId: string;
}) {
	const userSession = useUserSession();

	// Sorted so the categories cannot reorder the cache key and refetch identical data under a new one.
	const sortedCategories = [...categories].sort();

	return useQuery({
		enabled: enabled && !!userId,
		queryFn: () => {
			const categoryParams = sortedCategories.map(c => `&categories=${encodeURIComponent(c)}`).join('');
			const url = `admin/SuccessInbox/threads?userId=${encodeURIComponent(userId)}&openOnly=${openOnly}&urgentOnly=${urgentOnly}&supportWouldHelp=${supportWouldHelp}${categoryParams}`;
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<ISuccessInboxThread>>(url, 'GET');
		},
		meta: { toastErrorMessage: 'Could not load this inbox.' },
		queryKey: [
			SUCCESS_INBOX_THREADS_QUERY_KEY,
			userId,
			openOnly,
			sortedCategories.join(','),
			urgentOnly,
			supportWouldHelp,
		],
		refetchInterval,
	});
}

export interface ISuccessInboxMessage {
	providerMessageId: string;
	from?: string;
	receivedAt: string;
	text?: string;
	truncated: boolean;
}

/** The actual email on a thread. Read live from the rep's mailbox, so only fetched when asked for. */
export function useSuccessInboxThreadMessagesQuery({ enabled, threadId }: { enabled: boolean; threadId: string }) {
	const userSession = useUserSession();

	return useQuery({
		enabled: enabled && !!threadId,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISuccessInboxMessage[]>(
				`admin/SuccessInbox/threads/${encodeURIComponent(threadId)}/messages`,
				'GET'
			),
		meta: { toastErrorMessage: 'Could not read this email from the mailbox.' },
		queryKey: [SUCCESS_INBOX_THREADS_QUERY_KEY, 'messages', threadId],
		staleTime: 5 * 60 * 1000,
	});
}

/** What the escalation dialog opens with. Fetched on open, since it costs a mailbox-adjacent read. */
export function useSuccessInboxEscalationDraftQuery({ enabled, threadId }: { enabled: boolean; threadId: string }) {
	const userSession = useUserSession();

	return useQuery({
		enabled: enabled && !!threadId,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISuccessInboxEscalationDraft>(
				`admin/SuccessInbox/threads/${encodeURIComponent(threadId)}/escalation/draft`,
				'GET'
			),
		meta: { toastErrorMessage: 'Could not prepare this escalation.' },
		queryKey: [SUCCESS_INBOX_THREADS_QUERY_KEY, 'escalation-draft', threadId],
	});
}

export function useSuccessInboxBulkDispositionMutation({
	onError,
	onSuccess,
}: {
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: (updated: number) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ disposition, ids, note }: { disposition: SuccessInboxDisposition; ids: string[]; note?: string }) =>
			userSession.webServiceHelper.callAsync<{ failed: number; updated: number }>(
				'admin/SuccessInbox/threads/disposition',
				'PUT',
				{ disposition, ids, note }
			),
		onError,
		onSuccess: outcome => {
			queryClient.invalidateQueries({ queryKey: [SUCCESS_INBOX_THREADS_QUERY_KEY] });
			onSuccess?.(outcome?.updated ?? 0);
		},
	});
}

export function useSuccessInboxRepsQuery({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		queryFn: () => userSession.webServiceHelper.callAsync<ISuccessRep[]>('admin/SuccessInbox/pilot/effective', 'GET'),
		meta: { toastErrorMessage: 'Could not load the reps in scope.' },
		queryKey: [SUCCESS_INBOX_PILOT_QUERY_KEY],
	});
}

export function useSuccessInboxDispositionMutation({
	onError,
	onSuccess,
}: {
	onError?: (
		err: Api.IOperationResultNoValue,
		variables: { disposition: SuccessInboxDisposition; id: string; note?: string }
	) => void;
	onSuccess?: (thread: ISuccessInboxThread) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ disposition, id, note }: { disposition: SuccessInboxDisposition; id: string; note?: string }) => {
			const noteParam = note ? `&note=${encodeURIComponent(note)}` : '';
			const url = `admin/SuccessInbox/threads/${encodeURIComponent(id)}/disposition?disposition=${disposition}${noteParam}`;
			return userSession.webServiceHelper.callAsync<ISuccessInboxThread>(url, 'PUT');
		},
		onError,
		onSuccess: thread => {
			queryClient.invalidateQueries({ queryKey: [SUCCESS_INBOX_THREADS_QUERY_KEY] });
			onSuccess?.(thread);
		},
	});
}

export function useSuccessInboxEscalateMutation({
	onError,
	onSuccess,
}: {
	onError?: (err: Api.IOperationResultNoValue, variables: { id: string }) => void;
	onSuccess?: (thread: ISuccessInboxThread) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, request }: { id: string; request: ISuccessInboxEscalationRequest }) =>
			userSession.webServiceHelper.callAsync<ISuccessInboxThread>(
				`admin/SuccessInbox/threads/${encodeURIComponent(id)}/escalate`,
				'POST',
				request
			),
		onError,
		onSuccess: thread => {
			queryClient.invalidateQueries({ queryKey: [SUCCESS_INBOX_THREADS_QUERY_KEY] });
			onSuccess?.(thread);
		},
	});
}
